<!-- Ported from the Polaris kit: src/product/components/EditorDrawer.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09):
     - Frame: DrawerFrame.vue (PROVISÓRIO, R2-X-3) instead of the registry Sheet: a column from 1280px, the
       non-modal overlay below. In the column the kit's width is capped at 60% of the row, so the list keeps room.
     - The details column and the preview follow the room the drawer has, not the viewport (container queries):
       details beside the fields from a 48rem panel (the kit: `lg:`), the preview from a 64rem drawer (the kit:
       viewport ≥ 1280). Same markup otherwise.
     - The guard lives in useDiscardGuard.js + DiscardChangesAlert.vue (same words, same place); `requestClose` and
       `runGuarded` are exposed, so a page can switch rows with the drawer open without losing edits silently.
     - Added: `mode` (DrawerFrame), `titleEditable` (false shows the title as text, for a form that has its own name
       field), slot `actions` (replaces Enable/Save: an editor that saves by itself shows "Saved" there, 6-Q1).
     - `titlePlaceholder` and `saveLabel` default to i18n.
     - The kit's `pr-12`/`ml-auto` in the header are logical here (`pe-12`, `ms-auto`), so RTL mirrors them. -->
<script setup>
// The wide editor that "New" and "Edit" open: the title is a field, the state is said in words, and Enable, Save
// and Close sit in its header. Nothing exists before Save. Leaving with changes asks first, in the panel, never in
// a second modal. Slots: default (the fields), `details` (a column about the thing), `preview` (the companion panel,
// shown when there is room), `footer`, `actions`.
import { computed, useId, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DrawerFrame from './DrawerFrame.vue';
import DiscardChangesAlert from './DiscardChangesAlert.vue';
import { useDiscardGuard } from './useDiscardGuard';

const props = defineProps({
  kind: { type: String, required: true }, // what is being edited, in words: "Scenario"
  titlePlaceholder: { type: String, default: '' },
  titleEditable: { type: Boolean, default: true },
  status: { type: Object, default: null }, // { label, variant }
  dirty: Boolean,
  canSave: { type: Boolean, default: true },
  enableLabel: { type: String, default: '' }, // empty: the thing has no on and off
  saveLabel: { type: String, default: '' },
  mode: { type: String, default: 'auto' }, // DrawerFrame: 'auto' (column ≥ 1280, overlay below) or 'overlay'
});
const emit = defineEmits(['save', 'enable']);
const open = defineModel('open', { type: Boolean, default: false });
const title = defineModel('title', { type: String, default: '' });
const { t } = useI18n();
const slots = useSlots();

// As wide as what it holds: the fields alone (46rem), plus the details column, plus the preview. Never wider than
// the window (overlay) or than 60% of the row (column).
const overlayWidth = computed(() => {
  if (slots.preview) return 'sm:max-w-[min(70.625rem,calc(100vw-3rem))]';
  if (slots.details) return 'sm:max-w-[min(66rem,calc(100vw-3rem))]';
  return 'sm:max-w-[min(46rem,calc(100vw-3rem))]';
});
const columnWidth = computed(() => {
  if (slots.preview) return 'w-[min(70.625rem,60%)]';
  if (slots.details) return 'w-[min(66rem,60%)]';
  return 'w-[min(46rem,60%)]';
});
const placeholder = computed(
  () => props.titlePlaceholder || t('POLARIS.DRAWER.UNTITLED')
);
const label = computed(
  () => `${props.kind}: ${title.value || placeholder.value}`
);

const keepId = `editor-drawer-keep-${useId()}`;
const { confirming, requestClose, runGuarded, keepEditing, discard } =
  useDiscardGuard({
    isOpen: () => open.value,
    isDirty: () => props.dirty,
    close: () => {
      open.value = false;
    },
    keepId,
  });
defineExpose({ requestClose, runGuarded });
</script>

<template>
  <DrawerFrame
    :open="open"
    :label="label"
    :mode="mode"
    :column-class="columnWidth"
    :overlay-class="overlayWidth"
    data-editor-drawer
    @close="requestClose"
  >
    <p class="sr-only">{{ t('POLARIS.DRAWER.UNSAVED_NOTE') }}</p>
    <div class="flex min-h-0 flex-1 @container/drawer">
      <div class="flex min-w-0 flex-1 flex-col @container/main">
        <header
          class="flex min-h-16 shrink-0 flex-wrap items-center gap-2 border-b border-solid border-polaris-border px-6 py-3"
          :class="$slots.preview ? 'pe-12 @5xl/drawer:pe-6' : 'pe-12'"
        >
          <span class="type-polaris-body text-polaris-muted-foreground">
            {{ kind }}
          </span>
          <Input
            v-if="titleEditable"
            v-model="title"
            class="min-w-40 flex-1 sm:max-w-md"
            :aria-label="t('POLARIS.DRAWER.TITLE_LABEL', { kind })"
            :placeholder="placeholder"
          />
          <h2
            v-else
            class="min-w-0 flex-1 truncate type-polaris-section-title"
            :title="title || placeholder"
          >
            {{ title || placeholder }}
          </h2>
          <Badge v-if="status" :variant="status.variant ?? 'secondary'">
            {{ status.label }}
          </Badge>
          <div class="ms-auto flex items-center gap-2">
            <slot name="actions">
              <Button
                v-if="enableLabel"
                type="button"
                variant="outline"
                size="sm"
                @click="emit('enable')"
              >
                {{ enableLabel }}
              </Button>
              <Button
                type="button"
                size="sm"
                :disabled="!dirty || !canSave"
                @click="emit('save')"
              >
                {{ saveLabel || t('POLARIS.DRAWER.SAVE') }}
              </Button>
            </slot>
          </div>
        </header>
        <!-- With room the details are a column beside the fields; without it they follow the fields, in the same scroll. -->
        <div
          class="flex min-h-0 flex-1 flex-col overflow-auto @3xl/main:flex-row @3xl/main:overflow-hidden"
        >
          <div class="min-w-0 p-6 @3xl/main:flex-1 @3xl/main:overflow-auto">
            <DiscardChangesAlert
              v-if="confirming"
              :keep-id="keepId"
              size="sm"
              @keep="keepEditing"
              @discard="discard"
            />
            <div v-show="!confirming" class="grid gap-4"><slot /></div>
          </div>
          <aside
            v-if="$slots.details"
            v-show="!confirming"
            class="shrink-0 border-t border-solid border-polaris-border @3xl/main:w-80 @3xl/main:overflow-auto @3xl/main:border-t-0 ltr:@3xl/main:border-l rtl:@3xl/main:border-r"
            :aria-label="t('POLARIS.DRAWER.DETAILS')"
          >
            <slot name="details" />
          </aside>
        </div>
        <footer
          v-if="$slots.footer"
          class="shrink-0 border-t border-solid border-polaris-border px-6 py-3 type-polaris-caption text-polaris-muted-foreground"
        >
          <slot name="footer" />
        </footer>
      </div>
      <div
        v-if="$slots.preview"
        class="hidden w-[25rem] shrink-0 flex-col border-solid border-polaris-border pt-10 @5xl/drawer:flex ltr:border-l rtl:border-r"
      >
        <slot name="preview" />
      </div>
    </div>
  </DrawerFrame>
</template>
